import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

// Matches the Gastronovi Kassensystem Storno taxonomy — a single-select reason
// is required on every void so shift reports can roll cancellations up by
// reason instead of parsing free text (backend/reason_codes.py).
export const STORNO_REASONS = [
  { code: 'bedienerfehler', label: 'Bedienerfehler' },
  { code: 'reklamation', label: 'Reklamation' },
  { code: 'ausverkauft', label: 'Ausverkauft' },
  { code: 'gaestefehler', label: 'Gästefehler' },
  { code: 'bruch', label: 'Bruch' },
  { code: 'schwund', label: 'Schwund' },
  { code: 'verdorben', label: 'Verdorben' },
];

export const STORNO_REASON_LABELS = STORNO_REASONS.reduce(
  (acc, r) => ({ ...acc, [r.code]: r.label }),
  {}
);

// item: the cart line being voided (Storno of a single line, with an optional
// partial quantity). Pass item={null} for a whole-order Storno instead — the
// quantity stepper is hidden since "all of it" is implicit.
export default function StornoDialog({ open, onClose, item, onConfirm }) {
  const maxQty = item?.quantity || 1;
  const [quantity, setQuantity] = useState(maxQty);
  const [wasProduced, setWasProduced] = useState(true);
  const [reasonCode, setReasonCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setQuantity(item?.quantity || 1);
      setWasProduced(true);
      setReasonCode('');
    }
  }, [open, item]);

  const handleConfirm = async () => {
    if (!reasonCode) {
      toast.error('Bitte einen Storno-Grund auswählen');
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm({ quantity: item ? quantity : undefined, wasProduced, reasonCode });
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Stornierung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="pos-workflow-dialog max-w-sm mx-4 rounded-none border">
        <DialogHeader><DialogTitle>Stornieren</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {item ? (
            <div>
              <Label className="text-sm font-medium">Auswahl</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="pos-dialog-secondary flex h-10 w-10 items-center justify-center border"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="pos-dialog-primary flex h-10 flex-1 items-center justify-center font-bold">
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  className="pos-dialog-secondary flex h-10 w-10 items-center justify-center border"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setQuantity(maxQty)}
                  className="pos-dialog-secondary flex h-10 w-10 items-center justify-center border text-xs font-semibold"
                  title="Alle auswählen"
                >
                  A
                </button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">von {maxQty} · {item.product_name}</p>
            </div>
          ) : null}

          <div className="pos-dialog-surface flex items-center justify-between border px-3 py-2.5">
            <Label className="text-sm font-medium">Wurde produziert</Label>
            <Switch checked={wasProduced} onCheckedChange={setWasProduced} />
          </div>

          <div>
            <Label className="text-sm font-medium">Grund</Label>
            <RadioGroup value={reasonCode} onValueChange={setReasonCode} className="mt-2">
              {STORNO_REASONS.map((reason) => (
                <label
                  key={reason.code}
                  className={`flex cursor-pointer items-center justify-between border px-3 py-2.5 transition-colors ${
                    reasonCode === reason.code
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'pos-dialog-surface'
                  }`}
                >
                  <span className="text-sm">{reason.label}</span>
                  <RadioGroupItem value={reason.code} />
                </label>
              ))}
            </RadioGroup>
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            Wollen Sie den ausgewählten Eintrag und alle darin enthaltenen Posten wirklich stornieren?
          </p>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="pos-dialog-secondary h-12 flex-1 rounded-none border"
            >
              Schließen
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isSubmitting || !reasonCode}
              className="h-12 flex-1 rounded-none bg-rose-600 hover:bg-rose-700"
            >
              Stornieren
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
